import { Link } from "react-router-dom";
import { useSystemStatus } from "../api/systemStatus.js";

// Компактный «системный пульс» сверху List page. Один взгляд → понимаешь:
//   - aliveли воркер (queue нон-static)
//   - все ли cron'ы сегодня собрались (coverage)
//   - есть ли свежие фейлы (clickable → /failures)
//   - не упёрся ли acc в дневной лимит (capacity bar)

export function SystemStatusCard() {
  const q = useSystemStatus();
  if (q.isLoading || !q.data) return null;
  const s = q.data;

  // Самый красный сигнал из всех — определяет accent карточки.
  const hasFailures = s.jobs24h.failed > 0;
  const coverageGap = s.coverageToday.some((c) => c.scraped < c.expected);
  const capacityHigh = s.capacity.some((c) => c.percent >= 80);
  const alarm = hasFailures || coverageGap || capacityHigh;

  return (
    <div
      className={`rounded-xl border p-3 text-xs ${
        alarm
          ? "border-destructive/30 bg-destructive/5"
          : "border-bg-secondary bg-bg-secondary"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wide text-hint">
          {alarm ? "⚠ Внимание" : "✓ Система"}
        </div>
        <div className="flex gap-3 text-hint">
          <span>
            👤 <span className="text-text">{s.accounts.working}</span>/{s.accounts.active + s.accounts.cooldown}
          </span>
          <span>
            📋 <span className="text-text">{s.queue.wait + s.queue.delayed + s.queue.active}</span>
          </span>
        </div>
      </div>

      {/* Coverage today — только если есть что показать */}
      {s.coverageToday.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {s.coverageToday.map((c) => {
            const full = c.scraped >= c.expected;
            return (
              <span
                key={c.cron}
                className={`rounded-md px-1.5 py-0.5 font-mono text-[10px] ${
                  full ? "bg-bg text-text" : "bg-destructive/15 text-destructive"
                }`}
                title={`Сегодня: ${c.scraped}/${c.expected} собрано`}
              >
                {cronDayLabel(c.cron)} {c.scraped}/{c.expected}
              </span>
            );
          })}
        </div>
      )}

      {/* Jobs24h — успехи + фейлы (фейлы кликабельные → /failures) */}
      <div className="mt-2 flex items-center justify-between">
        <div className="text-hint">
          24ч: <span className="text-text">{s.jobs24h.success}</span> ✓
        </div>
        {hasFailures ? (
          <Link
            to="/failures"
            className="rounded-md bg-destructive/20 px-2 py-0.5 font-medium text-destructive"
          >
            {s.jobs24h.failed} ✕ →
          </Link>
        ) : (
          <span className="text-hint">0 ✕</span>
        )}
      </div>

      {/* Capacity bars per active acc — только если кто-то ≥ 60% */}
      {s.capacity.some((c) => c.percent >= 60) && (
        <div className="mt-2 space-y-1">
          {s.capacity
            .filter((c) => c.percent >= 60)
            .map((c) => (
              <div key={c.accountId}>
                <div className="flex items-center justify-between text-[10px] text-hint">
                  <span className="truncate">{c.login}</span>
                  <span>
                    {c.dailyRequestCount}/{c.dailyLimit} ({c.percent}%)
                  </span>
                </div>
                <div className="mt-0.5 h-1 overflow-hidden rounded-full bg-bg">
                  <div
                    className={`h-full ${
                      c.percent >= 80 ? "bg-destructive" : "bg-button"
                    }`}
                    style={{ width: `${Math.min(100, c.percent)}%` }}
                  />
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

// «0 4 * * 6» → «Сб», «0 4 1 * *» → «1-е», «0 4 * * *» → «day».
function cronDayLabel(cron: string): string {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron;
  const [, , dom, , dow] = parts;
  if (dom !== "*" && !isNaN(parseInt(dom!, 10))) return `${dom}-е`;
  if (dow !== "*" && !isNaN(parseInt(dow!, 10))) {
    return ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"][parseInt(dow!, 10)] ?? cron;
  }
  return "every";
}
